// ============================================================================
// Auth Provider - App-Level Authentication Initialization
// EduPath Africa v2.0 - Routing Architecture
// ============================================================================

import React, { useEffect, useState } from 'react';
import { useAuthStore } from '@/stores/authStore';

// ============================================================================
// Loading Screen
// ============================================================================

const AppLoadingScreen: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100">
    <div className="text-center">
      {/* Logo */}
      <div className="mb-6">
        <img 
          src="/edupath-logo.png" 
          alt="EduPath Africa" 
          className="h-16 mx-auto"
          onError={(e) => {
            // Fallback if logo doesn't load
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      </div>
      
      {/* Spinner */}
      <div className="relative">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-200 border-t-primary-600 mx-auto"></div>
      </div>
      
      {/* Text */}
      <p className="mt-4 text-primary-700 font-medium">Loading EduPath...</p>
    </div>
  </div>
);

// ============================================================================
// Error Boundary for Auth
// ============================================================================

interface AuthErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class AuthErrorBoundary extends React.Component<
  { children: React.ReactNode },
  AuthErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): AuthErrorBoundaryState {
    return { hasError: true, error };
  }

  handleReset = () => {
    // Clear auth state and reload
    localStorage.removeItem('edupath-auth');
    sessionStorage.clear();
    window.location.href = '/login';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center max-w-md px-4">
            <div className="text-6xl mb-4">⚠️</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Authentication Error
            </h1>
            <p className="text-gray-600 mb-6">
              Something went wrong with your session. Please try logging in again.
            </p>
            <button
              onClick={this.handleReset}
              className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Return to Login
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// ============================================================================
// Auth Provider Component
// ============================================================================

interface AuthProviderProps {
  children: React.ReactNode;
  
  /**
   * Custom loading component.
   */
  loadingComponent?: React.ReactNode;
  
  /**
   * Callback when auth is ready.
   */
  onReady?: () => void;
  
  /**
   * Timeout for auth initialization (ms).
   * @default 5000
   */
  timeout?: number;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({
  children,
  loadingComponent,
  onReady,
  timeout = 5000,
}) => {
  const isHydrated = useAuthStore(state => state.isHydrated);
  const [timedOut, setTimedOut] = useState(false);
  
  // ------------------------------------------------------------------
  // Timeout Handler
  // ------------------------------------------------------------------
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isHydrated) {
        console.warn('Auth hydration timed out, forcing hydrated state');
        setTimedOut(true);
        // Force hydration to prevent infinite loading
        useAuthStore.getState().setHydrated();
      }
    }, timeout);
    
    return () => clearTimeout(timer);
  }, [timeout, isHydrated]);
  
  // ------------------------------------------------------------------
  // Ready Callback
  // ------------------------------------------------------------------
  useEffect(() => {
    if (isHydrated && onReady) {
      onReady();
    }
  }, [isHydrated, onReady]);
  
  // ------------------------------------------------------------------
  // Show loading until hydrated
  // ------------------------------------------------------------------
  if (!isHydrated && !timedOut) {
    return <>{loadingComponent || <AppLoadingScreen />}</>;
  }
  
  return (
    <AuthErrorBoundary>
      {children}
    </AuthErrorBoundary>
  );
};

// ============================================================================
// Auth Initializer Hook
// ============================================================================

/**
 * Hook to run auth-related side effects on app mount.
 * Call this in your App component.
 */
export function useAuthInitializer() {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const accessToken = useAuthStore(state => state.accessToken);
  const refreshAuth = useAuthStore(state => state.refreshAuth);
  
  // Only refresh if we have a token - don't refresh immediately on mount
  // This prevents race conditions with hydration
  useEffect(() => {
    // Skip if not authenticated or no token
    if (!isAuthenticated || !accessToken) return;
    
    // Set up token refresh interval (every 15 minutes)
    // Don't refresh immediately - let the existing token work
    const interval = setInterval(() => {
      refreshAuth().catch((err) => {
        console.warn('Token refresh failed (will retry):', err);
        // Don't logout on refresh failure - let the 401 handler do it
      });
    }, 15 * 60 * 1000); // 15 minutes
    
    return () => clearInterval(interval);
  }, [isAuthenticated, accessToken, refreshAuth]);
}

// ============================================================================
// Debug Helper (remove in production)
// ============================================================================

export function useAuthDebug() {
  const state = useAuthStore();
  
  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('[Auth Debug]', {
        isHydrated: state.isHydrated,
        isAuthenticated: state.isAuthenticated,
        isLoading: state.isLoading,
        user: state.user ? {
          id: state.user.id,
          email: state.user.email,
          role: state.user.role,
          tier: state.user.entitlementLevel,
        } : null,
      });
    }
  }, [state.isHydrated, state.isAuthenticated, state.isLoading, state.user]);
}

export default AuthProvider;
